import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { AlertCircle, CheckCircle, Database, Play, Trash2, Plus } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import { authService } from '../../services/authService';

interface JoinTable {
  table: string;
  type: 'INNER' | 'LEFT' | 'RIGHT' | 'FULL';
  on: string;
}

interface QueryResult {
  success: boolean;
  message: string;
  data?: any[];
  columns?: string[];
  affectedRows?: number;
}

const SuperAdminSqlQuery: React.FC = () => {
  const { toast } = useToast();
  const user = authService.getCurrentUser();

  // States para query builder
  const [operation, setOperation] = useState<'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE'>('SELECT');
  const [selectedEmpresa, setSelectedEmpresa] = useState<string>('');
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [joins, setJoins] = useState<JoinTable[]>([]);
  const [whereClause, setWhereClause] = useState<string>('');
  const [orderByClause, setOrderByClause] = useState<string>('');
  const [limitClause, setLimitClause] = useState<string>('');

  // States para query livre
  const [freeQuery, setFreeQuery] = useState<string>('');
  const [useFreeQuery, setUseFreeQuery] = useState<boolean>(false);

  // States para resultados
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // States para dados estáticos
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [tables, setTables] = useState<any[]>([]);
  const [tableColumns, setTableColumns] = useState<any[]>([]);
  const [availableTables, setAvailableTables] = useState<any[]>([]);

  // Carregar empresas
  useEffect(() => {
    const loadEmpresas = async () => {
      // Verificar se o usuário é SuperAdmin
      if (user?.role !== 'SuperAdmin') {
        console.log('[DEBUG] SuperAdminSqlQuery - Usuário não é SuperAdmin:', user?.role);
        toast({
          title: 'Acesso Negado',
          description: 'Apenas usuários SuperAdmin podem acessar esta funcionalidade',
          variant: 'destructive',
        });
        return;
      }

      try {
        console.log('[DEBUG] SuperAdminSqlQuery - Carregando empresas...');
        const response = await api.get('/empresas', {
          params: {
            page: 1,
            pageSize: 100, // Carregar todas as empresas de uma vez
            sortBy: 'nome',
            sortOrder: 'asc'
          }
        });
        const empresasData = response.data?.data || response.data?.empresas || response.data || [];
        setEmpresas(Array.isArray(empresasData) ? empresasData : [empresasData].filter(Boolean));
        console.log('[DEBUG] SuperAdminSqlQuery - Empresas carregadas:', empresasData.length);
      } catch (error: any) {
        console.error('Erro ao carregar empresas:', error);
        if (error?.response?.status === 403) {
          toast({
            title: 'Acesso Negado',
            description: 'Você não tem permissão para acessar esta funcionalidade',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Erro',
            description: 'Não foi possível carregar as empresas',
            variant: 'destructive',
          });
        }
      }
    };
    loadEmpresas();
  }, []);

  // Carregar tabelas quando o componente montar (não depende mais de empresa)
  useEffect(() => {
    loadTables();
  }, []);

  // Carregar colunas quando tabela é selecionada
  useEffect(() => {
    if (selectedTable && selectedEmpresa) {
      loadTableColumns();
    }
  }, [selectedTable, selectedEmpresa]);

  const loadTables = async () => {
    try {
      console.log('[DEBUG] SuperAdminSqlQuery - Carregando tabelas (sem dependência de empresa)');
      const response = await api.get('/superadmin/tables');
      const tablesData = response.data?.tables || response.data || [];
      setTables(Array.isArray(tablesData) ? tablesData : [tablesData].filter(Boolean));
      setAvailableTables(Array.isArray(tablesData) ? tablesData : [tablesData].filter(Boolean));
      console.log('[DEBUG] SuperAdminSqlQuery - Tabelas carregadas:', tablesData.length);
    } catch (error) {
      console.error('Erro ao carregar tabelas:', error);
      setTables([]);
      setAvailableTables([]);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as tabelas',
        variant: 'destructive',
      });
    }
  };

  const loadTableColumns = async () => {
    try {
      console.log('[DEBUG] SuperAdminSqlQuery - Carregando colunas para tabela:', selectedTable, 'empresa:', selectedEmpresa);
      const response = await api.get(`/superadmin/columns/${selectedTable}`);
      const columnsData = response.data?.columns || response.data || [];
      setTableColumns(Array.isArray(columnsData) ? columnsData : [columnsData].filter(Boolean));
      console.log('[DEBUG] SuperAdminSqlQuery - Colunas carregadas:', columnsData.length);
    } catch (error) {
      console.error('Erro ao carregar colunas:', error);
      setTableColumns([]);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as colunas da tabela',
        variant: 'destructive',
      });
    }
  };

  const addJoin = () => {
    setJoins([...joins, { table: '', type: 'INNER', on: '' }]);
  };

  const removeJoin = (index: number) => {
    setJoins(joins.filter((_, i) => i !== index));
  };

  const updateJoin = (index: number, field: keyof JoinTable, value: any) => {
    const newJoins = [...joins];
    newJoins[index] = { ...newJoins[index], [field]: value };
    setJoins(newJoins);
  };

  const buildQuery = (): string => {
    if (useFreeQuery) {
      return freeQuery;
    }

    let query = '';

    switch (operation) {
      case 'SELECT':
        const columns = selectedColumns.length > 0 ? selectedColumns.join(', ') : '*';
        query = `SELECT ${columns} FROM ${selectedTable}`;

        // Adicionar JOINs
        joins.forEach(join => {
          if (join.table) {
            query += ` ${join.type} JOIN ${join.table} ON ${join.on || '1=1'}`;
          }
        });

        // Adicionar WHERE
        if (whereClause.trim()) {
          query += ` WHERE ${whereClause}`;
        }

        // Adicionar ORDER BY
        if (orderByClause.trim()) {
          query += ` ORDER BY ${orderByClause}`;
        }

        // Adicionar LIMIT
        if (limitClause.trim()) {
          query += ` LIMIT ${limitClause}`;
        }
        break;

      case 'INSERT':
        query = `INSERT INTO ${selectedTable} `;
        // Aqui você precisaria de campos para especificar valores
        break;

      case 'UPDATE':
        query = `UPDATE ${selectedTable} SET `;
        // Aqui você precisaria de campos para especificar valores
        if (whereClause.trim()) {
          query += ` WHERE ${whereClause}`;
        }
        break;

      case 'DELETE':
        query = `DELETE FROM ${selectedTable}`;
        if (whereClause.trim()) {
          query += ` WHERE ${whereClause}`;
        }
        break;
    }

    return query;
  };

  const executeQuery = async () => {
    const query = buildQuery();

    if (!query.trim()) {
      toast({
        title: 'Erro',
        description: 'Digite uma consulta SQL válida',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setQueryResult(null);

    try {
      const response = await api.post('/superadmin/execute-query', {
        query: query
      });

      setQueryResult(response.data);

      toast({
        title: response.data.success ? 'Sucesso' : 'Atenção',
        description: response.data.message,
        variant: response.data.success ? 'default' : 'destructive',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Erro ao executar consulta';
      setQueryResult({
        success: false,
        message: errorMessage
      });

      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setQueryResult(null);
    setFreeQuery('');
    setWhereClause('');
    setOrderByClause('');
    setLimitClause('');
    setSelectedColumns([]);
    setJoins([]);
  };

  const toggleColumn = (column: string) => {
    setSelectedColumns(prev =>
      prev.includes(column)
        ? prev.filter(c => c !== column)
        : [...prev, column]
    );
  };

  return (
    <div className="space-y-6">
      <Card className="p-3 sm:p-6 dark:bg-slate-800">
        <div className="flex items-center gap-2 mb-6">
          <Database className="h-5 w-5" />
          <h2 className="text-lg sm:text-xl font-semibold">SuperAdmin - Consultas SQL</h2>
        </div>

        {/* Switch entre Query Builder e Query Livre */}
        <div className="flex items-center space-x-2 mb-6">
          <Switch
            id="use-free-query"
            checked={useFreeQuery}
            onCheckedChange={setUseFreeQuery}
          />
          <Label htmlFor="use-free-query">Usar Query Livre</Label>
        </div>

        {!useFreeQuery ? (
          /* Query Builder Interface */
          <div className="space-y-4">
            {/* Operação */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label htmlFor="operation">Operação</Label>
              <Select value={operation} onValueChange={(value: any) => setOperation(value)}>
                <SelectTrigger className="sm:col-span-3">
                  <SelectValue placeholder="Selecione a operação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SELECT">SELECT</SelectItem>
                  <SelectItem value="INSERT">INSERT</SelectItem>
                  <SelectItem value="UPDATE">UPDATE</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Empresa */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label htmlFor="empresa">Empresa</Label>
              <Select value={selectedEmpresa} onValueChange={setSelectedEmpresa}>
                <SelectTrigger className="sm:col-span-3">
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas && Array.isArray(empresas) && empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tabela */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label htmlFor="table">Tabela</Label>
              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger className="sm:col-span-3">
                  <SelectValue placeholder="Selecione a tabela" />
                </SelectTrigger>
                <SelectContent>
                  {tables && Array.isArray(tables) && tables.map((table) => (
                    <SelectItem key={table.name} value={table.name}>
                      {table.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Colunas (apenas para SELECT) */}
            {operation === 'SELECT' && selectedTable && (
              <div className="space-y-2">
                <Label>Colunas</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-md dark:bg-slate-700">
                  {tableColumns && Array.isArray(tableColumns) && tableColumns.map((column) => (
                    <Badge
                      key={column.name}
                      variant={selectedColumns.includes(column.name) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleColumn(column.name)}
                    >
                      {column.name}
                    </Badge>
                  ))}
                </div>
                <p className="text-sm text-gray-500">
                  Clique nas colunas para selecioná-las. Se nenhuma for selecionada, usará *
                </p>
              </div>
            )}

            {/* JOINs (apenas para SELECT) */}
            {operation === 'SELECT' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>JOINs</Label>
                  <Button onClick={addJoin} variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar JOIN
                  </Button>
                </div>

                {joins && Array.isArray(joins) && joins.map((join, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-12 gap-2 p-3 border rounded-md dark:bg-slate-700">
                    <Select value={join.type} onValueChange={(value: any) => updateJoin(index, 'type', value)}>
                      <SelectTrigger className="sm:col-span-3">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INNER">INNER</SelectItem>
                        <SelectItem value="LEFT">LEFT</SelectItem>
                        <SelectItem value="RIGHT">RIGHT</SelectItem>
                        <SelectItem value="FULL">FULL</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={join.table} onValueChange={(value) => updateJoin(index, 'table', value)}>
                      <SelectTrigger className="sm:col-span-4">
                        <SelectValue placeholder="Tabela" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTables && Array.isArray(availableTables) && availableTables.map((table) => (
                          <SelectItem key={table.name} value={table.name}>
                            {table.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Input
                      placeholder="ON (ex: table1.id = table2.id)"
                      value={join.on}
                      onChange={(e) => updateJoin(index, 'on', e.target.value)}
                      className="sm:col-span-4"
                    />

                    <Button
                      onClick={() => removeJoin(index)}
                      variant="outline"
                      size="sm"
                      className="sm:col-span-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* WHERE */}
            <div className="space-y-2">
              <Label htmlFor="where">WHERE (opcional)</Label>
              <Textarea
                id="where"
                placeholder="Ex: status = 'active' AND created_at > '2024-01-01'"
                value={whereClause}
                onChange={(e) => setWhereClause(e.target.value)}
                className="dark:bg-slate-700"
              />
            </div>

            {/* ORDER BY (apenas para SELECT) */}
            {operation === 'SELECT' && (
              <div className="space-y-2">
                <Label htmlFor="orderBy">ORDER BY (opcional)</Label>
                <Textarea
                  id="orderBy"
                  placeholder="Ex: created_at DESC, name ASC"
                  value={orderByClause}
                  onChange={(e) => setOrderByClause(e.target.value)}
                  className="dark:bg-slate-700"
                />
              </div>
            )}

            {/* LIMIT (apenas para SELECT) */}
            {operation === 'SELECT' && (
              <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
                <Label htmlFor="limit">LIMIT (opcional)</Label>
                <Input
                  id="limit"
                  type="number"
                  placeholder="100"
                  value={limitClause}
                  onChange={(e) => setLimitClause(e.target.value)}
                  className="sm:col-span-3 dark:bg-slate-700"
                />
              </div>
            )}
          </div>
        ) : (
          /* Query Livre */
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label htmlFor="empresa">Empresa</Label>
              <Select value={selectedEmpresa} onValueChange={setSelectedEmpresa}>
                <SelectTrigger className="sm:col-span-3">
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas && Array.isArray(empresas) && empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="freeQuery">Query SQL</Label>
              <Textarea
                id="freeQuery"
                placeholder="Digite sua consulta SQL aqui..."
                value={freeQuery}
                onChange={(e) => setFreeQuery(e.target.value)}
                className="min-h-[200px] font-mono dark:bg-slate-700"
              />
            </div>
          </div>
        )}

        {/* Botões de ação */}
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button
            onClick={executeQuery}
            disabled={loading || !selectedEmpresa || (!useFreeQuery && !selectedTable)}
            className="flex items-center gap-2"
          >
            <Play className="h-4 w-4" />
            {loading ? 'Executando...' : 'Executar Query'}
          </Button>

          <Button
            onClick={clearResults}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Limpar
          </Button>
        </div>

        {/* Preview da Query */}
        {!useFreeQuery && (selectedTable || joins.length > 0 || whereClause || orderByClause) && (
          <div className="mt-6 p-4 bg-gray-100 dark:bg-slate-700 rounded-md">
            <Label className="text-sm font-medium">Query Preview:</Label>
            <pre className="mt-2 text-sm font-mono overflow-x-auto">
              {buildQuery()}
            </pre>
          </div>
        )}
      </Card>

      {/* Resultados */}
      {queryResult && (
        <Card className="p-3 sm:p-6 dark:bg-slate-800">
          <div className="flex items-center gap-2 mb-4">
            {queryResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-500" />
            )}
            <h3 className="text-lg font-semibold">Resultado</h3>
          </div>

          <div className="mb-4">
            <p className={queryResult.success ? "text-green-600" : "text-red-600"}>
              {queryResult.message}
            </p>
            {queryResult.affectedRows !== undefined && (
              <p className="text-sm text-gray-500 mt-1">
                Linhas afetadas: {queryResult.affectedRows}
              </p>
            )}
          </div>

          {/* Tabela de resultados: uma única tabela com rolagem horizontal/vertical (cabeçalho fixo) */}
          {queryResult.data && queryResult.data.length > 0 && (
            <div className="w-full border rounded-md overflow-auto max-h-[350px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-slate-700">
                  <tr className="border-b">
                    {queryResult.columns && Array.isArray(queryResult.columns) && queryResult.columns.map((column) => (
                      <th key={column} className="whitespace-nowrap px-4 py-2 text-left font-medium text-zinc-500 dark:text-zinc-300">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {queryResult.data && Array.isArray(queryResult.data) && queryResult.data.map((row, index) => (
                    <tr key={index} className="border-b last:border-0">
                      {queryResult.columns && Array.isArray(queryResult.columns) && queryResult.columns.map((column) => {
                        const cellValue = row[column] !== null ? String(row[column]) : 'NULL';
                        return (
                          <td key={column} className="whitespace-nowrap px-4 py-2 max-w-[300px] truncate" title={cellValue}>
                            {cellValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {queryResult.data && queryResult.data.length === 0 && (
            <p className="text-gray-500 italic">Nenhum resultado encontrado</p>
          )}
        </Card>
      )}
    </div>
  );
};

export default SuperAdminSqlQuery;
